/**
 * Feature Flags API Service
 * Handles admin/feature-flags and admin/feature-flag-groups endpoints
 */

import axiosInstance from '@/lib/axios'
import API_ROUTES from '@/lib/api-routes'
import type {
    CreateFeatureFlagGroupRequest,
    CreateFeatureFlagRequest,
    DeleteResponse,
    FeatureFlagDetailResponse,
    FeatureFlagFilters,
    FeatureFlagGroupDetailResponse,
    FeatureFlagGroupFilters,
    FeatureFlagGroupListResponse,
    FeatureFlagGroupMutationResponse,
    FeatureFlagListResponse,
    FeatureFlagMutationResponse,
    FeatureFlagPreviewResponse,
    FeatureFlagStatus,
    GroupPreviewResponse,
    PreviewFeatureFlagRequest,
    TargetableFieldsResponse,
    UpdateFeatureFlagGroupRequest,
    UpdateFeatureFlagRequest,
} from '@/types/feature-flag'

/**
 * The API wraps every response as `{ status, statusCode, message, data }` via a
 * global interceptor. That interceptor unwraps one level for handlers that
 * return a `message` of their own (our mutations), but leaves handlers that
 * don't (our list endpoints, which return `pagination` instead) double-wrapped.
 *
 * This peels whichever shape came back down to the handler's own payload, so
 * every function below resolves to the type it declares.
 */
const unwrap = <T>(body: unknown): T => {
    const envelope = body as { message?: string; data?: unknown }
    const inner = envelope?.data

    // Double-wrapped: the handler's own `{ success, data, pagination }` is here.
    if (inner && typeof inner === 'object' && 'success' in inner) {
        return inner as T
    }

    // Already unwrapped by the interceptor — rebuild the handler's shape.
    return {
        success: true,
        message: envelope?.message,
        data: inner,
    } as T
}

/**
 * List feature flags
 * GET /admin/feature-flags
 */
export const getFeatureFlags = async (
    filters?: FeatureFlagFilters
): Promise<FeatureFlagListResponse> => {
    const response = await axiosInstance.get(API_ROUTES.FEATURE_FLAGS.LIST, {
        params: filters,
    })
    return unwrap<FeatureFlagListResponse>(response.data)
}

/**
 * Get a single feature flag
 * GET /admin/feature-flags/{id}
 */
export const getFeatureFlagById = async (
    id: string
): Promise<FeatureFlagDetailResponse> => {
    const response = await axiosInstance.get(
        API_ROUTES.FEATURE_FLAGS.DETAIL(id)
    )
    return unwrap<FeatureFlagDetailResponse>(response.data)
}

/**
 * Fields and operators the rule builder may offer
 * GET /admin/feature-flags/fields
 */
export const getTargetableFields =
    async (): Promise<TargetableFieldsResponse> => {
        const response = await axiosInstance.get(
            API_ROUTES.FEATURE_FLAGS.FIELDS
        )
        return unwrap<TargetableFieldsResponse>(response.data)
    }

/**
 * Create a feature flag
 * POST /admin/feature-flags
 */
export const createFeatureFlag = async (
    data: CreateFeatureFlagRequest
): Promise<FeatureFlagMutationResponse> => {
    const response = await axiosInstance.post(
        API_ROUTES.FEATURE_FLAGS.CREATE,
        data
    )
    return unwrap<FeatureFlagMutationResponse>(response.data)
}

/**
 * Update a feature flag
 * PATCH /admin/feature-flags/{id}
 */
export const updateFeatureFlag = async (
    id: string,
    data: UpdateFeatureFlagRequest
): Promise<FeatureFlagMutationResponse> => {
    const response = await axiosInstance.patch(
        API_ROUTES.FEATURE_FLAGS.UPDATE(id),
        data
    )
    return unwrap<FeatureFlagMutationResponse>(response.data)
}

/**
 * Turn a flag on or off without touching its targeting
 * PATCH /admin/feature-flags/{id}/toggle
 */
export const toggleFeatureFlag = async (
    id: string,
    enabled: boolean
): Promise<FeatureFlagMutationResponse> => {
    const response = await axiosInstance.patch(
        API_ROUTES.FEATURE_FLAGS.TOGGLE(id),
        { enabled }
    )
    return unwrap<FeatureFlagMutationResponse>(response.data)
}

/**
 * Update the status of several flags at once
 * PATCH /admin/feature-flags/status/bulk
 */
export const bulkUpdateFeatureFlagStatus = async (
    ids: string[],
    status: FeatureFlagStatus
): Promise<DeleteResponse> => {
    const response = await axiosInstance.patch(
        API_ROUTES.FEATURE_FLAGS.BULK_STATUS,
        { ids, status }
    )
    return unwrap<DeleteResponse>(response.data)
}

/**
 * Evaluate a flag as a specific user/client
 * POST /admin/feature-flags/{id}/preview
 */
export const previewFeatureFlag = async (
    id: string,
    data: PreviewFeatureFlagRequest
): Promise<FeatureFlagPreviewResponse> => {
    const response = await axiosInstance.post(
        API_ROUTES.FEATURE_FLAGS.PREVIEW(id),
        data
    )
    return unwrap<FeatureFlagPreviewResponse>(response.data)
}

/**
 * Delete a feature flag
 * DELETE /admin/feature-flags/{id}
 */
export const deleteFeatureFlag = async (
    id: string
): Promise<DeleteResponse> => {
    const response = await axiosInstance.delete(
        API_ROUTES.FEATURE_FLAGS.DELETE(id)
    )
    return unwrap<DeleteResponse>(response.data)
}

/* ================= Groups ================= */

/**
 * List feature flag groups
 * GET /admin/feature-flag-groups
 */
export const getFeatureFlagGroups = async (
    filters?: FeatureFlagGroupFilters
): Promise<FeatureFlagGroupListResponse> => {
    const response = await axiosInstance.get(
        API_ROUTES.FEATURE_FLAG_GROUPS.LIST,
        {
            params: filters,
        }
    )
    return unwrap<FeatureFlagGroupListResponse>(response.data)
}

/**
 * Get a single group
 * GET /admin/feature-flag-groups/{id}
 */
export const getFeatureFlagGroupById = async (
    id: string
): Promise<FeatureFlagGroupDetailResponse> => {
    const response = await axiosInstance.get(
        API_ROUTES.FEATURE_FLAG_GROUPS.DETAIL(id)
    )
    return unwrap<FeatureFlagGroupDetailResponse>(response.data)
}

/**
 * Create a group
 * POST /admin/feature-flag-groups
 */
export const createFeatureFlagGroup = async (
    data: CreateFeatureFlagGroupRequest
): Promise<FeatureFlagGroupMutationResponse> => {
    const response = await axiosInstance.post(
        API_ROUTES.FEATURE_FLAG_GROUPS.CREATE,
        data
    )
    return unwrap<FeatureFlagGroupMutationResponse>(response.data)
}

/**
 * Update a group
 * PATCH /admin/feature-flag-groups/{id}
 */
export const updateFeatureFlagGroup = async (
    id: string,
    data: UpdateFeatureFlagGroupRequest
): Promise<FeatureFlagGroupMutationResponse> => {
    const response = await axiosInstance.patch(
        API_ROUTES.FEATURE_FLAG_GROUPS.UPDATE(id),
        data
    )
    return unwrap<FeatureFlagGroupMutationResponse>(response.data)
}

/**
 * Count the users a group covers
 * POST /admin/feature-flag-groups/{id}/preview
 */
export const previewFeatureFlagGroup = async (
    id: string
): Promise<GroupPreviewResponse> => {
    const response = await axiosInstance.post(
        API_ROUTES.FEATURE_FLAG_GROUPS.PREVIEW(id)
    )
    return unwrap<GroupPreviewResponse>(response.data)
}

/**
 * Delete a group
 * DELETE /admin/feature-flag-groups/{id}
 */
export const deleteFeatureFlagGroup = async (
    id: string
): Promise<DeleteResponse> => {
    const response = await axiosInstance.delete(
        API_ROUTES.FEATURE_FLAG_GROUPS.DELETE(id)
    )
    return unwrap<DeleteResponse>(response.data)
}
